import { getMeVisibilityGroupsTeamsOptions } from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { NativeSelect } from "@/components/ui/NativeSelect";
import { useAppContext } from "@/hooks/use-app-context";

type VisibilityGroupSelectProps = {
  value: string | null;
  onChange: (value: string | null) => void;
};

/**
 * Formats the visibility group name to a readable format.
 */
const formatVisibilityGroup = (
  visibilityGroup: string,
  orgNameMap: Map<string, string>,
): string => {
  if (visibilityGroup.startsWith("ORG")) {
    const orgName = orgNameMap.get(visibilityGroup);
    return orgName || "My organization";
  } else {
    return visibilityGroup
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }
};

/**
 * VisibilityGroupSelect - Dropdown for selecting a visibility group
 *
 * Uses a native select to avoid Radix DismissableLayer conflicts
 * with the Autocomplete popover inside the same Dialog.
 * Auto-selects the first organization-based visibility group if no value is set.
 */
export const VisibilityGroupSelect = ({
  value,
  onChange,
}: VisibilityGroupSelectProps) => {
  const { data: visibilityGroups, isLoading } = useQuery({
    ...getMeVisibilityGroupsTeamsOptions(),
    select: (result) => result?.visibilityGroups ?? [],
  });

  const { userOrganizations } = useAppContext();

  const orgNameMap = useMemo(() => {
    const map = new Map<string, string>();
    userOrganizations.forEach((org) => {
      map.set(org.orgId, org.orgName);
    });
    return map;
  }, [userOrganizations]);

  const items = useMemo(() => {
    if (!visibilityGroups) return [];
    return visibilityGroups.map((group: string) => ({
      value: group,
      label: formatVisibilityGroup(group, orgNameMap),
    }));
  }, [visibilityGroups, orgNameMap]);

  // Auto-select first org visibility group when no value is set
  useEffect(() => {
    if (!value && visibilityGroups && visibilityGroups.length > 0) {
      const firstOrg =
        visibilityGroups.find((vg) => vg.startsWith("ORG")) ??
        visibilityGroups[0];
      onChange(firstOrg);
    }
  }, [value, visibilityGroups, onChange]);

  if (isLoading) {
    return (
      <div className="flex h-10 items-center rounded-sm border border-border-input bg-input px-3">
        <span className="text-sm text-placeholder">Loading groups...</span>
      </div>
    );
  }

  return (
    <NativeSelect
      value={value || undefined}
      onValueChange={(val) => onChange(val || null)}
      items={items}
      placeholder="Select Visibility Group"
    />
  );
};
